import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { registerQuery, useQuery } from 'data-fetching-client';
import styled from 'styled-components';
import classNames from 'classnames';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { BATTLESHIP, EERIE, GYPSUM } from 'HubStyleTokens/colors';
import { H2 } from 'UIComponents/elements/headings';
import UIModal from 'UIComponents/dialog/UIModal';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import UIWizard from 'UIComponents/dialog/UIWizard';
import UIFlex from 'UIComponents/layout/UIFlex';
import UICloseButton from 'UIComponents/button/UICloseButton';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIIllustration from 'UIComponents/image/UIIllustration';
import { fetchUserInsights, searchProfiles } from '../../../clients/profilesClient';
/* @ts-expect-error module is untyped */

import { push } from '../../../utils/history';
/* @ts-expect-error module is untyped */

import { getCatalogServiceTrackingName } from '../../../utils/i18n';
import { fromFilterStateToLocation, fromFilterStateToQuery } from '../../../utils/storefrontFilters';
import { storefrontFiltersInitialState } from '../../../slices/storefrontFiltersSlice';
import { getServices } from '../../../slices/serviceCatalogSlice';
import StorefrontUsageTrackerContext from '../../../context/StorefrontUsageTrackerContext';
import { COUNTRY, SERVICE } from '../../../constants/search/FilterKey';
/* @ts-expect-error module is untyped */

import { OfficeLocationType } from '../../../constants/search/Filters';
import { RELEVANCE_DESC } from '../../../constants/search/SortField';
import * as FilterKey from '../../../constants/search/FilterKey';
import ServiceStep from './FindPartnerWizardServiceStep';
import IndustryStep from './FindPartnerWizardIndustryStep';
import CountryStep from './FindPartnerWizardCountryStep';
import LanguageStep from './FindPartnerWizardLanguageStep';
import FindPartnerWizardFooter from './FindPartnerWizardFooter';
import { getRecentActivity, setRecentActivity } from '../../../utils/homepage';
export const WIZARD_STEP_HEIGHT = 350;
export const WIZARD_STEP_CLASSNAME = 'p-x-14';
const StyledHeaderWrapper = styled.span.withConfig({
  displayName: "FindPartnerWizard__StyledHeaderWrapper",
  componentId: "wh8qnm-0"
})(["background-color:", ";border-width:0 0 1px 0;border-style:solid;border-color:", ";border-top-right-radius:3px;border-top-left-radius:3px;width:100%;"], GYPSUM, BATTLESHIP);
const StepComponents = [ServiceStep, IndustryStep, CountryStep, LanguageStep];
const StepKeys = ['service', 'industry', 'country', 'language'];
const GET_PROFILES = registerQuery({
  args: ['filters'],
  fieldName: 'profiles',
  fetcher: ({
    filters
  }) => {
    return searchProfiles(fromFilterStateToQuery(filters));
  }
});
const GET_USER_INSIGHTS = registerQuery({
  fieldName: 'insights',
  fetcher: () => fetchUserInsights()
});
export default function FindPartnerWizard({
  isMobileView,
  onClose
}) {
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const catalogServices = useSelector(getServices);
  const [step, setStep] = useState(0);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [filters, setFilters] = useState(Object.assign({}, storefrontFiltersInitialState, {
    [FilterKey.SORTS]: RELEVANCE_DESC
  }));
  const [profileCount, setProfileCount] = useState(0);
  const [showProfileCount, setShowProfileCount] = useState(false);
  const trackQuestionnaireInteraction = useCallback((action, subscreen) => {
    trackInteraction(action, Object.assign({}, storefrontFiltersInitialState, {}, filters, {
      service: filters[SERVICE].map(id => getCatalogServiceTrackingName(id, catalogServices)),
      subscreen
    }));
  }, [catalogServices, filters, trackInteraction]);
  const handleClickSkip = useCallback(() => {
    const stepKey = StepKeys[step];
    setFilters(Object.assign({}, filters, {
      [stepKey]: storefrontFiltersInitialState[stepKey]
    }));
    setStep(step + 1);
    trackQuestionnaireInteraction('skip questionnaire step', stepKey);
  }, [filters, step, trackQuestionnaireInteraction]);
  const handleClickNext = useCallback(({
    target: {
      value
    }
  }) => {
    const stepKey = StepKeys[step];
    setStep(value); // don't set 'type' field until the questionnaire is finished

    setRecentActivity({
      questionnaireFilters: filters
    });
    trackQuestionnaireInteraction('save questionnaire step', stepKey);
  }, [filters, step, trackQuestionnaireInteraction]);
  const handleClickSubmit = useCallback(closeConfirmation => {
    onClose();
    setRecentActivity({
      type: 'questionnaire',
      questionnaireFilters: filters,
      viewedProfiles: []
    });
    trackQuestionnaireInteraction('submit questionnaire', closeConfirmation ? 'close confirmation' : 'language');
    const {
      pathname,
      search
    } = fromFilterStateToLocation(filters, catalogServices);
    push({
      pathname,
      search: `?ref=questionnaire${search.replace('?', '&')}`
    });
  }, [catalogServices, filters, onClose, trackQuestionnaireInteraction]);
  useEffect(() => {
    const storedFilters = getRecentActivity();

    if (storedFilters && storedFilters.questionnaireFilters) {
      setFilters(storedFilters.questionnaireFilters);
    }
  }, []);
  const {
    data: profilesData
  } = useQuery(GET_PROFILES, {
    variables: {
      filters
    }
  });
  const {
    data: insightsData
  } = useQuery(GET_USER_INSIGHTS);
  const userInsights = useMemo(() => insightsData && insightsData.insights, [insightsData]);
  useEffect(() => {
    if (profilesData) {
      setProfileCount(profilesData.profiles[0].total);
      setShowProfileCount(!!filters.service.length || !!filters.industry.length || !!filters.country || !!filters.language.length);
    }
  }, [profilesData, filters]);
  const WizardFooter = useCallback((_ref) => {
    let props = Object.assign({}, _ref);
    return /*#__PURE__*/_jsx(FindPartnerWizardFooter, Object.assign({}, props, {
      disableNextButton: !filters[StepKeys[step]].length,
      onClickSkip: handleClickSkip
    }));
  }, [filters, handleClickSkip, step]);
  const WizardHeader = useCallback(({
    wizardProps
  }) => {
    const {
      onReject
    } = wizardProps;
    return /*#__PURE__*/_jsxs(StyledHeaderWrapper, {
      className: "m-bottom-5 " + (showProfileCount ? 'p-y-5' : 'p-y-8'),
      children: [/*#__PURE__*/_jsx(UINanoProgress, {
        "aria-label": unescapedText('ecosystem-marketplace-solutions-ui.homepage.questionnaire.progressBarAccessibleLabel'),
        value: (step + 1) / StepComponents.length * 100,
        height: 6
      }), showProfileCount && /*#__PURE__*/_jsx(UIFlex, {
        align: "center",
        direction: "column",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.homepage.questionnaire.matching",
          options: {
            count: profileCount
          }
        })
      }), /*#__PURE__*/_jsx(UICloseButton, {
        color: EERIE,
        onClick: () => onReject()
      })]
    });
  }, [profileCount, showProfileCount, step]);
  const handleFilterChange = useCallback((key, value) => {
    let newFilters = Object.assign({}, filters, {
      [key]: value
    });

    if (key === COUNTRY && value === 'ANY') {
      newFilters = Object.assign({}, newFilters, {
        office_location: OfficeLocationType.ANY
      });
    }

    setFilters(newFilters);
  }, [filters]);
  const renderedCloseConfirmation = useMemo(() => {
    return /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      direction: "column",
      style: {
        height: WIZARD_STEP_HEIGHT + 173
      },
      children: [/*#__PURE__*/_jsx(WizardHeader, {
        stepProps: {},
        wizardProps: {
          stepIndex: step,
          onReject: () => onClose()
        }
      }), /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        className: classNames("text-center p-bottom-15", WIZARD_STEP_CLASSNAME),
        direction: "column",
        children: [/*#__PURE__*/_jsx(UIIllustration, {
          name: "approvals",
          width: "30%"
        }), /*#__PURE__*/_jsx(H2, {
          className: "m-top-5",
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "ecosystem-marketplace-solutions-ui.homepage.questionnaire.close.title"
          })
        }), /*#__PURE__*/_jsxs(UIFlex, {
          align: "center",
          className: "m-top-5",
          direction: isMobileView ? 'column' : 'row',
          children: [/*#__PURE__*/_jsx(UIButton, {
            onClick: () => setShowCloseConfirmation(false),
            use: "primary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ecosystem-marketplace-solutions-ui.homepage.questionnaire.close.continue"
            })
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            className: "m-x-3",
            message: "ecosystem-marketplace-solutions-ui.homepage.questionnaire.close.or"
          }), /*#__PURE__*/_jsxs(UIButton, {
            onClick: () => handleClickSubmit(true),
            use: "secondary",
            children: [/*#__PURE__*/_jsx(UIIcon, {
              name: "search",
              size: "xxs"
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ecosystem-marketplace-solutions-ui.homepage.questionnaire.close.search"
            })]
          })]
        }), /*#__PURE__*/_jsx(UIButton, {
          className: "m-top-5 m-bottom-10",
          onClick: () => onClose(),
          use: "unstyled",
          style: {
            textDecoration: 'underline'
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ecosystem-marketplace-solutions-ui.homepage.questionnaire.close.close"
          })
        })]
      })]
    });
  }, [handleClickSubmit, isMobileView, onClose, step, WizardHeader]);
  return /*#__PURE__*/_jsx(UIModal, {
    width: 700,
    children: showCloseConfirmation ? renderedCloseConfirmation : /*#__PURE__*/_jsx(UIWizard, {
      footerComponent: WizardFooter,
      headerComponent: WizardHeader,
      onConfirm: handleClickSubmit,
      onStepIndexChange: handleClickNext,
      onReject: () => setShowCloseConfirmation(true),
      stepIndex: step,
      children: StepComponents.map((StepComponent, index) => /*#__PURE__*/_jsx(StepComponent, {
        filters: filters,
        onFilterChange: handleFilterChange,
        userInsights: userInsights
      }, index))
    })
  });
}